import React, { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Typography,
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Tooltip,
  SvgIcon,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LanguageIcon from '@mui/icons-material/Language';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import YouTubeIcon from '@mui/icons-material/YouTube';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAppSettings } from '../../state/providers/AppSettingsProvider';
import {
  Act,
  Vendor,
  fetchActsFromDirectus,
  fetchActsFromDirectusByClientSlug,
  fetchVendorsFromDirectus,
  fetchVendorsFromDirectusByClientSlug,
} from '../../services/directus/directusClient';
import { getClientSlugFromHostname } from '../../utils/getClientSlugFromHostname';
import styles from './EventListingsDialog.module.css';

export interface EventListingsDialogProps {
  open: boolean;
  onClose: () => void;
}

type SocialType = 'website' | 'instagram' | 'facebook' | 'x' | 'tiktok' | 'youtube' | 'spotify';

const TikTokIcon: React.FC = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M19.59 7.34c-1.45-.87-2.53-2.1-3.03-3.8-.11-.37-.19-.75-.23-1.15h-3.18v13.1c0 1.25-1.02 2.27-2.27 2.27s-2.27-1.02-2.27-2.27 1.02-2.27 2.27-2.27c.23 0 .46.03.68.1V9.96c-.22-.03-.45-.05-.68-.05A5.52 5.52 0 0 0 5.36 15.4a5.52 5.52 0 0 0 5.5 5.51 5.52 5.52 0 0 0 5.51-5.51V8.57c1.22.88 2.72 1.41 4.36 1.48V6.93c-.4-.02-.79-.12-1.14-.32Z"
    />
  </SvgIcon>
);

const SpotifyIcon: React.FC = () => (
  <SvgIcon viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2Zm4.59 14.44a.75.75 0 0 1-1.03.25c-2.82-1.72-6.37-2.11-10.56-1.15a.75.75 0 1 1-.34-1.46c4.57-1.05 8.48-.6 11.62 1.32.36.22.48.68.31 1.04Zm1.3-2.88a.9.9 0 0 1-1.24.3c-3.23-1.99-8.15-2.56-11.96-1.4a.9.9 0 1 1-.52-1.72c4.35-1.32 9.74-.68 13.45 1.6.42.26.55.81.27 1.22Zm.11-3.02c-3.88-2.3-10.29-2.51-14-.1a1.05 1.05 0 0 1-1.14-1.76c4.29-2.77 11.41-2.55 15.95.15a1.05 1.05 0 0 1-.81 1.71Z"
    />
  </SvgIcon>
);

const EventListingsDialog: React.FC<EventListingsDialogProps> = ({ open, onClose }) => {
  const { settings } = useAppSettings();
  const clientId = (settings?.client_id ?? '').trim();
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const clientSlug = (getClientSlugFromHostname(hostname) ?? '').trim();

  const [acts, setActs] = useState<Act[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [vendorCarouselIndex, setVendorCarouselIndex] = useState<Record<string, number>>({});

  const canFetch = useMemo(() => Boolean(open && (clientId || clientSlug)), [open, clientId, clientSlug]);

  useEffect(() => {
    if (!canFetch) {
      return;
    }

    let isCancelled = false;
    const run = async () => {
      setLoading(true);
      setErrorMessage(null);
      try {
        const [actsResult, vendorsResult] = await Promise.all(
          clientId
            ? [fetchActsFromDirectus(clientId), fetchVendorsFromDirectus(clientId)]
            : [fetchActsFromDirectusByClientSlug(clientSlug), fetchVendorsFromDirectusByClientSlug(clientSlug)],
        );

        if (isCancelled) {
          return;
        }

        setActs(actsResult);
        setVendors(vendorsResult);
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const message = err instanceof Error ? err.message : 'Failed to load event listings.';
        setErrorMessage(message);
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      isCancelled = true;
    };
  }, [canFetch, clientId, clientSlug]);

  const buildSocialLinks = (act: Act): Array<{ type: SocialType; label: string; url: string }> => {
    const links: Array<{ type: SocialType; label: string; url: string }> = [];
    if (act.website) links.push({ type: 'website', label: 'Website', url: act.website });
    if (act.instagram) links.push({ type: 'instagram', label: 'Instagram', url: act.instagram });
    if (act.facebook) links.push({ type: 'facebook', label: 'Facebook', url: act.facebook });
    if (act.x) links.push({ type: 'x', label: 'X', url: act.x });
    if (act.tiktok) links.push({ type: 'tiktok', label: 'TikTok', url: act.tiktok });
    if (act.youtube) links.push({ type: 'youtube', label: 'YouTube', url: act.youtube });
    if (act.spotify) links.push({ type: 'spotify', label: 'Spotify', url: act.spotify });
    return links;
  };

  const buildVendorSocialLinks = (vendor: Vendor): Array<{ type: SocialType; label: string; url: string }> => {
    const links: Array<{ type: SocialType; label: string; url: string }> = [];
    if (vendor.instagram) links.push({ type: 'instagram', label: 'Instagram', url: vendor.instagram });
    if (vendor.facebook) links.push({ type: 'facebook', label: 'Facebook', url: vendor.facebook });
    return links;
  };

  const getVendorCarouselImages = (vendor: Vendor): string[] => {
    const urls = [vendor.logo, vendor.image ].filter((u): u is string => Boolean(u));
    return Array.from(new Set(urls));
  };

  const renderSocialIcon = (type: SocialType) => {
    switch (type) {
      case 'website':
        return <LanguageIcon fontSize="small" />;
      case 'instagram':
        return <InstagramIcon fontSize="small" />;
      case 'facebook':
        return <FacebookIcon fontSize="small" />;
      case 'x':
        return <TwitterIcon fontSize="small" />;
      case 'tiktok':
        return <TikTokIcon />;
      case 'youtube':
        return <YouTubeIcon fontSize="small" />;
      case 'spotify':
        return <SpotifyIcon />;
      default:
        return null;
    }
  };

  if (!open) {
    return null;
  }

  return (
    <Box className={styles.container} onClick={onClose}>
      <Box className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <Box className={styles.header}>
          <Typography
            sx={{
              fontWeight: 700,
              color: 'var(--primary-colour)',
              fontSize: '1.6rem',
              lineHeight: 1.2,
              fontFamily: '"Work Sans", serif',
            }}
          >
            Event Listings
          </Typography>
          <IconButton onClick={onClose} className={styles.closeButton} aria-label="Close">
            <CloseIcon sx={{ fontSize: '1.7rem' }} />
          </IconButton>
        </Box>

        <Box className={styles.content}>
          <Container maxWidth="md" disableGutters>
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'var(--secondary-colour)',
                mx: '-18px',
                px: '18px',
                pt: '14px',
                pb: 1.5,
                borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 10px 22px rgba(0, 0, 0, 0.28)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 95%, white)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid var(--primary-colour-alpha-30)',
                  borderRadius: '12px',
                  padding: '4px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                  gap: '4px',
                }}
              >
                <Box
                  onClick={() => setTabIndex(0)}
                  sx={{
                    flex: 1,
                    padding: '8px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: tabIndex === 0 ? 'var(--primary-colour)' : 'transparent',
                    color: tabIndex === 0 ? 'var(--primary-text-colour)' : 'rgba(255, 255, 255, 0.7)',
                    fontWeight: tabIndex === 0 ? 600 : 400,
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: tabIndex === 0 ? 'var(--primary-colour)' : 'var(--primary-colour-alpha-20)',
                    },
                  }}
                >
                  Acts ({acts.length})
                </Box>
                <Box
                  onClick={() => setTabIndex(1)}
                  sx={{
                    flex: 1,
                    padding: '8px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    backgroundColor: tabIndex === 1 ? 'var(--primary-colour)' : 'transparent',
                    color: tabIndex === 1 ? 'var(--primary-text-colour)' : 'rgba(255, 255, 255, 0.7)',
                    fontWeight: tabIndex === 1 ? 600 : 400,
                    fontSize: '0.875rem',
                    textAlign: 'center',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: tabIndex === 1 ? 'var(--primary-colour)' : 'var(--primary-colour-alpha-20)',
                    },
                  }}
                >
                  Vendors ({vendors.length})
                </Box>
              </Box>
            </Box>
          {loading && (
            <Typography sx={{ color: 'var(--primary-colour)' }}>Loading…</Typography>
          )}

          {errorMessage && (
            <Typography sx={{ color: 'var(--primary-colour)' }}>{errorMessage}</Typography>
          )}

          {!loading && !errorMessage && tabIndex === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 1.25 }}>
              {acts.length === 0 && (
                <Typography sx={{ color: 'var(--primary-colour)', opacity: 0.9 }}>
                  No acts found.
                </Typography>
              )}
              {acts.map((act) => (
                <Card
                  key={act.id}
                  elevation={2}
                  sx={{
                    backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 92%, white)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 40px rgba(0, 0, 0, 0.35), 0 2px 10px rgba(0, 0, 0, 0.25)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {act.image && (
                      <Box
                        component="img"
                        src={act.image}
                        alt={act.name}
                        sx={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 110,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.18), rgba(0,0,0,0))',
                        pointerEvents: 'none',
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: 14,
                        right: 14,
                        bottom: 12,
                        fontWeight: 900,
                        color: 'var(--primary-colour)',
                        fontSize: '1.35rem',
                        lineHeight: 1.15,
                        textShadow: '0 2px 10px rgba(0,0,0,0.65)',
                      }}
                    >
                      {act.name}
                    </Typography>
                  </Box>
                  <CardContent>
                    {act.time && (
                      <Typography sx={{ color: 'var(--primary-colour)', fontWeight: 800, opacity: 0.95, textTransform: 'uppercase' }}>
                        {act.time}
                      </Typography>
                    )}
                    {act.description && (
                      <Typography sx={{ color: 'var(--primary-colour)', lineHeight: 1.55, opacity: 0.9 }}>
                        {act.description}
                      </Typography>
                    )}

                    {buildSocialLinks(act).length > 0 && (
                      <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {buildSocialLinks(act).map((link) => (
                          <Tooltip key={link.type} title={link.label} arrow>
                            <IconButton
                              aria-label={link.label}
                              onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                              sx={{
                                border: '1px solid rgba(255, 255, 255, 0.18)',
                                borderRadius: '12px',
                                color: 'var(--primary-colour)',
                                backgroundColor: 'rgba(0, 0, 0, 0.28)',
                                backdropFilter: 'blur(6px)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                },
                              }}
                            >
                              {renderSocialIcon(link.type)}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}

          {!loading && !errorMessage && tabIndex === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: 1.25 }}>
              {vendors.length === 0 && (
                <Typography sx={{ color: 'var(--primary-colour)', opacity: 0.9 }}>
                  No vendors found.
                </Typography>
              )}
              {vendors.map((vendor) => (
                <Card
                  key={vendor.id}
                  elevation={2}
                  sx={{
                    backgroundColor: 'color-mix(in srgb, var(--secondary-colour) 92%, white)',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    transition: 'transform 160ms ease, box-shadow 160ms ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 14px 40px rgba(0, 0, 0, 0.35), 0 2px 10px rgba(0, 0, 0, 0.25)',
                    },
                  }}
                >
                  <Box sx={{ position: 'relative' }}>
                    {(() => {
                      const images = getVendorCarouselImages(vendor);
                      const currentIndex = vendorCarouselIndex[vendor.id] ?? 0;
                      const safeIndex = images.length > 0 ? ((currentIndex % images.length) + images.length) % images.length : 0;
                      const currentImage = images[safeIndex];

                      return (
                        <>
                          {currentImage && (
                            <Box
                              component="img"
                              src={currentImage}
                              alt={vendor.name}
                              sx={{ width: '100%', height: 280, objectFit: 'cover', display: 'block' }}
                            />
                          )}

                          {images.length > 1 && (
                            <>
                              <IconButton
                                aria-label="Previous image"
                                onClick={() =>
                                  setVendorCarouselIndex((prev) => ({
                                    ...prev,
                                    [vendor.id]: (prev[vendor.id] ?? 0) - 1,
                                  }))
                                }
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: 10,
                                  transform: 'translateY(-50%)',
                                  width: 40,
                                  height: 40,
                                  borderRadius: '12px',
                                  color: 'var(--primary-colour)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                  border: '1px solid rgba(255, 255, 255, 0.18)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                  },
                                }}
                              >
                                <ChevronLeftIcon />
                              </IconButton>
                              <IconButton
                                aria-label="Next image"
                                onClick={() =>
                                  setVendorCarouselIndex((prev) => ({
                                    ...prev,
                                    [vendor.id]: (prev[vendor.id] ?? 0) + 1,
                                  }))
                                }
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: 10,
                                  transform: 'translateY(-50%)',
                                  width: 40,
                                  height: 40,
                                  borderRadius: '12px',
                                  color: 'var(--primary-colour)',
                                  backgroundColor: 'rgba(0, 0, 0, 0.55)',
                                  border: '1px solid rgba(255, 255, 255, 0.18)',
                                  '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                                  },
                                }}
                              >
                                <ChevronRightIcon />
                              </IconButton>

                              <Box
                                sx={{
                                  position: 'absolute',
                                  left: 0,
                                  right: 0,
                                  bottom: 8,
                                  display: 'flex',
                                  justifyContent: 'center',
                                  gap: 0.75,
                                  pointerEvents: 'none',
                                }}
                              >
                                {images.map((_, idx) => (
                                  <Box
                                    key={idx}
                                    sx={{
                                      width: idx === safeIndex ? 16 : 7,
                                      height: 7,
                                      borderRadius: 999,
                                      backgroundColor:
                                        idx === safeIndex ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.35)',
                                      transition: 'all 160ms ease',
                                    }}
                                  />
                                ))}
                              </Box>
                            </>
                          )}
                        </>
                      );
                    })()}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 110,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0.18), rgba(0,0,0,0))',
                        pointerEvents: 'none',
                      }}
                    />
                    <Typography
                      sx={{
                        position: 'absolute',
                        left: 14,
                        right: 14,
                        bottom: 12,
                        fontWeight: 900,
                        color: 'var(--primary-colour)',
                        fontSize: '1.35rem',
                        lineHeight: 1.15,
                        textShadow: '0 2px 10px rgba(0,0,0,0.65)',
                        textTransform: 'uppercase',
                      }}
                    >
                      {vendor.name}
                    </Typography>
                  </Box>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: 1 }}>
                      {vendor.menu_file && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => window.open(vendor.menu_file, '_blank', 'noopener,noreferrer')}
                          sx={{
                            borderColor: 'var(--primary-colour)',
                            color: 'var(--primary-colour)',
                            textTransform: 'none',
                            fontWeight: 900,
                            borderRadius: '10px',
                            '&:hover': {
                              borderColor: 'var(--primary-colour)',
                              backgroundColor: 'var(--primary-colour-alpha-20)',
                            },
                          }}
                        >
                          View menu
                        </Button>
                      )}
                    </Box>
                    {vendor.description && (
                      <Typography sx={{ mt: 1, color: '#fff', lineHeight: 1.55, opacity: 0.9 }}>
                        {vendor.description}
                      </Typography>
                    )}

                    {buildVendorSocialLinks(vendor).length > 0 && (
                      <Box sx={{ mt: 1.25, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {buildVendorSocialLinks(vendor).map((link) => (
                          <Tooltip key={link.type} title={link.label} arrow>
                            <IconButton
                              aria-label={link.label}
                              onClick={() => window.open(link.url, '_blank', 'noopener,noreferrer')}
                              sx={{
                                border: '1px solid rgba(255, 255, 255, 0.18)',
                                borderRadius: '12px',
                                color: 'var(--primary-colour)',
                                backgroundColor: 'rgba(0, 0, 0, 0.28)',
                                backdropFilter: 'blur(6px)',
                                '&:hover': {
                                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                },
                              }}
                            >
                              {renderSocialIcon(link.type)}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default EventListingsDialog;
